import {
  baseApi,
  getQueryArgs,
  throwErrorFromResponseData,
  transformErrorResponse,
} from 'store/api'
import {
  PostGroupsBody,
  PutGroupBody,
  ImportJsonBody,
  GroupsResponse,
  PostGroupsResponse,
  GroupJsonResponse,
} from './groups.interface'
import { ApiResponse } from '../http'
import { profilesApi, ProfileSectionType, updateProfileSectionCount } from '../profiles'
import { current } from '@reduxjs/toolkit'
import uniqBy from 'lodash/uniqBy'

export const replaceEmptyViaInRequestBody = <T>(
  requestBody: T & { via?: string; via_v6?: string },
): T => {
  const body = {
    ...requestBody,
  }

  if (body?.via === '') {
    body.via = '-1'
  }

  if (body?.via_v6 === '') {
    body.via_v6 = '-1'
  }

  return body
}

export const groupApi = baseApi.injectEndpoints({
  endpoints: builder => ({
    getGroups: builder.query({
      query: ({
        profileId,
        ignoreImpersonation = false,
      }: {
        profileId: string | number
        ignoreImpersonation?: boolean
      }) => {
        const queryArgs = getQueryArgs(`/profiles/${profileId}/groups`, 'GET')
        if (ignoreImpersonation) {
          queryArgs.headers = undefined
        }
        return queryArgs
      },
      transformResponse: (response: GroupsResponse) => {
        throwErrorFromResponseData(response)

        return response.body
      },
      transformErrorResponse,
      providesTags: ['Group'],
    }),
    postGroup: builder.mutation({
      query: ({
        body,
        profileId,
        ignoreImpersonation,
      }: {
        body: PostGroupsBody
        profileId: string
        ignoreImpersonation?: boolean
      }) => {
        const queryArgs = getQueryArgs(
          `/profiles/${profileId}/groups`,
          'POST',
          replaceEmptyViaInRequestBody<PostGroupsBody>(body),
        )

        if (ignoreImpersonation) {
          queryArgs.headers = undefined
        }
        return queryArgs
      },
      transformResponse: (response: PostGroupsResponse) => {
        throwErrorFromResponseData(response)

        return response.body
      },
      async onQueryStarted({ profileId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          dispatch(
            groupApi.util.updateQueryData('getGroups', { profileId }, draft => {
              draft.groups.push(...data.groups)
              draft.groups?.sort((a, b) => {
                if (a.group.toString() < b.group.toString()) {
                  return -1
                }
                if (a.group.toString() > b.group.toString()) {
                  return 1
                }
                return 0
              })
            }),
          )
        } catch {}
      },
    }),
    putGroup: builder.mutation({
      query: ({
        group,
        body,
        profileId,
      }: {
        group: number
        body: PutGroupBody
        profileId: string
      }) => getQueryArgs(`/profiles/${profileId}/groups/${group}`, 'PUT', body),
      transformResponse: (response: PostGroupsResponse) => {
        throwErrorFromResponseData(response)

        return response.body
      },
      transformErrorResponse,
      async onQueryStarted({ profileId }, { dispatch, queryFulfilled }) {
        try {
          const { data } = await queryFulfilled

          dispatch(
            groupApi.util.updateQueryData('getGroups', { profileId }, draft => {
              const updatedGroup = data.groups[0]

              const currentGroupIndex = draft.groups.findIndex(f => f.PK === updatedGroup.PK)
              draft.groups[currentGroupIndex] = updatedGroup
            }),
          )
        } catch {}
      },
    }),
    deleteGroup: builder.mutation({
      query: ({
        profileId,
        group,
        ignoreImpersonation,
      }: {
        profileId: string
        group: number
        ignoreImpersonation?: boolean
      }) => {
        const queryArgs = getQueryArgs(`/profiles/${profileId}/groups/${group}`, 'DELETE')

        if (ignoreImpersonation) {
          queryArgs.headers = undefined
        }
        return queryArgs
      },
      transformResponse: (response: PostGroupsResponse) => {
        return response.body
      },
      transformErrorResponse,
      async onQueryStarted({ profileId, group }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled

          dispatch(
            groupApi.util.updateQueryData('getGroups', { profileId }, draft => {
              const deletedGroup = draft.groups.find(x => x.PK === group)

              if (deletedGroup) {
                dispatch(
                  profilesApi.util.updateQueryData('getProfiles', '', draft => {
                    updateProfileSectionCount({
                      draft,
                      profileId,
                      type: ProfileSectionType.CUSTOM_RULES,
                      count: current(deletedGroup)?.count,
                      isIncrease: false,
                    })
                  }),
                )
              }

              draft.groups = draft.groups.filter(x => x.PK !== group)
            }),
          )
        } catch {}
      },
    }),
    getJsonGroup: builder.query({
      query: ({ profileId, group }: { profileId: string; group: number }) =>
        getQueryArgs(`/profiles/${profileId}/groups/${group}/export/json`, 'GET'),
      transformResponse: (response: GroupJsonResponse) => {
        return response
      },
      transformErrorResponse,
    }),
    postJsonGroup: builder.mutation({
      query: ({ body, profileId }: { body: ImportJsonBody; profileId: string }) =>
        getQueryArgs(`/profiles/${profileId}/groups/import`, 'POST', body),
      transformResponse: (response: ApiResponse<ImportJsonBody>) => {
        return response.body
      },
      transformErrorResponse,
      async onQueryStarted({ profileId, body }, { dispatch, queryFulfilled }) {
        try {
          await queryFulfilled
          const uniqRules = uniqBy(body.config?.rules || [], 'PK')

          if (uniqRules?.length) {
            dispatch(
              profilesApi.util.updateQueryData('getProfiles', '', draft => {
                updateProfileSectionCount({
                  draft,
                  profileId,
                  type: ProfileSectionType.CUSTOM_RULES,
                  count: uniqRules?.length,
                  isIncrease: true,
                })
              }),
            )
          }
        } catch {}
      },
    }),
  }),
})

export const {
  useGetGroupsQuery,
  usePostGroupMutation,
  usePutGroupMutation,
  useDeleteGroupMutation,
  useLazyGetJsonGroupQuery,
  usePostJsonGroupMutation,
} = groupApi
